import React, { useState, useRef, useEffect } from 'react';
import { User, LogOut, Settings, Loader2, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useNavigate } from 'react-router-dom';
import { useUserData } from '../../hooks/useUserData';

interface UserProfileDropdownProps {
  className?: string;
  isMobile?: boolean;
}

const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({ className = '', isMobile = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { userInfo } = useUserData();
  
  // Check if user is an approved affiliate
  const isApprovedAffiliate = userInfo?.is_affiliate === true && userInfo?.affiliate_status === 'approved';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    setIsLoggingOut(true);
    
    // Add a small delay for better UX feedback
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    try {
      await logout();
      setIsOpen(false);
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoggingOut(false);
    }
  };

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  // Get user profile image or fallback to default
  const getProfileImage = () => {
    if (user?.profile_image) {
      return user.profile_image;
    }
    return null;
  };

  const profileImage = getProfileImage();

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Profile Picture Button */}
      <button
        onClick={toggleDropdown}
        className={`flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          isMobile ? 'w-full px-4 py-3 space-x-3 justify-start' : 'w-10 h-10'
        }`}
      >
        {profileImage ? (
          <>
            <img
              src={profileImage}
              alt="Profile"
              className={`rounded-full object-cover ${
                isMobile ? 'w-8 h-8' : 'w-10 h-10'
              }`}
              onError={(e) => {
                // Fallback to user icon if image fails to load
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const fallback = target.nextElementSibling as HTMLElement;
                if (fallback) fallback.style.display = 'flex';
              }}
            />
            {/* Fallback icon - hidden by default, shown when image fails */}
            <User 
              className={`text-gray-600 ${
                isMobile ? 'w-5 h-5' : 'w-6 h-6'
              }`} 
              style={{ display: 'none' }}
            />
          </>
        ) : (
          <User className={`text-gray-600 ${
            isMobile ? 'w-5 h-5' : 'w-6 h-6'
          }`} />
        )}
        {isMobile && (
          <span className="text-sm font-medium text-gray-700">
            Profile
          </span>
        )}
      </button>

      {/* Dropdown Menu - Only show for desktop */}
      {isOpen && !isMobile && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
          {/* Menu Items */}
          <div className="py-1">
            <Link
              to="/my-account"
              onClick={() => setIsOpen(false)}
              className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
            >
              <Settings className="w-4 h-4" />
              <span>My Account</span>
            </Link>
            
            {/* Only show Affiliate Program link for approved affiliates */}
            {isApprovedAffiliate && (
              <Link
                to="/affiliate"
                onClick={() => setIsOpen(false)}
                className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
              >
                <UserPlus className="w-4 h-4" />
                <span>Affiliate Program</span>
              </Link>
            )}
            
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="flex items-center space-x-3 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoggingOut ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <LogOut className="w-4 h-4" />
              )}
              <span>{isLoggingOut ? 'Please wait..' : 'Logout'}</span>
            </button>
          </div>
        </div>
      )}

      {/* Mobile Menu Items - Show as separate buttons below profile button */}
      {isOpen && isMobile && (
        <div className="mt-2 space-y-2">
          <Link
             to="/my-account"
             onClick={() => setIsOpen(false)}
             className="flex items-center space-x-3 w-full px-4 py-3 bg-gray-50 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
           >
            <Settings className="w-4 h-4" />
            <span>My Account</span>
          </Link>
          
          {/* Only show Affiliate Program link for approved affiliates */}
          {isApprovedAffiliate && (
            <Link
               to="/affiliate"
               onClick={() => setIsOpen(false)}
               className="flex items-center space-x-3 w-full px-4 py-3 bg-gray-50 rounded-lg text-sm text-gray-700 hover:bg-gray-100 transition-colors duration-200"
             >
              <UserPlus className="w-4 h-4" />
              <span>Affiliate Program</span>
            </Link>
          )}
          
          <button
             onClick={handleLogout}
             disabled={isLoggingOut}
             className="flex items-center space-x-3 w-full px-4 py-3 bg-red-50 rounded-lg text-sm text-red-600 hover:bg-red-100 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
           >
             {isLoggingOut ? (
               <Loader2 className="w-4 h-4 animate-spin" />
             ) : (
               <LogOut className="w-4 h-4" />
             )}
             <span>{isLoggingOut ? 'Please wait..' : 'Logout'}</span>
           </button>
        </div>
      )}
    </div>
  );
};

export default UserProfileDropdown;